import {
    Box,
    Skeleton,
    SortDirection,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { ReactElement, MouseEvent } from "react"
import { KeyedMutator } from "swr"

import InstallerDraftRow from "./installerDraftRow"
import {
    ASC,
    DESC,
    ORDER_BY_TIMESTAMP,
} from "../../../constants/generalConstants"
import {
    TT_ADDRESS,
    TT_TECHNOLOGY,
    TT_LAST_UPDATED,
    TT_COMPLETION_STATUS,
    TT_REVIEW_SUBMIT,
    TT_DELETE,
    TT_DRAFT_POSTCODE,
    TT_DRAFT_ID,
} from "../../../constants/tooltips"
import {
    IDraftApplicationListResponse,
    IDraftApplications,
} from "../../../services/middleware/types/installerInterface/installerApplicationTypes"
import ZeroApplicationsMsg from "../../fallback/zeroApplicationsMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import LctAvatar from "../../pills/lct/lctAvatar"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const avatarSx = { width: "70px", pl: 1 }
export const AvatarHeadingCell = (): ReactElement => (
    <AppTableCell sx={avatarSx} />
)

interface IAvatarContentCell {
    lcts: string[]
}
export const AvatarContentCell = ({
    lcts,
}: IAvatarContentCell): ReactElement => (
    <AppTableCell sx={avatarSx}>
        <LctAvatar lcts={lcts} />
    </AppTableCell>
)

interface IHeading {
    title: string
    tooltip: string
    sortId: string
    center: boolean
    enableSort: boolean
    xlOnly: boolean
    sortKey?: string
}

const HEADINGS: IHeading[] = [
    {
        title: "",
        tooltip: "",
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
    {
        title: "Address",
        tooltip: TT_ADDRESS,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
    {
        title: "Post Code",
        tooltip: TT_DRAFT_POSTCODE,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: "postCode",
        xlOnly: false,
    },
    {
        title: "Draft ID",
        tooltip: TT_DRAFT_ID,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: "draftId",
        xlOnly: false,
    },
    {
        title: "Last Updated",
        tooltip: TT_LAST_UPDATED,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: "lastUpdated",
        xlOnly: false,
    },
    {
        title: "Completion Status",
        tooltip: TT_COMPLETION_STATUS,
        center: true,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
    {
        title: "Technology",
        tooltip: TT_TECHNOLOGY,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
    {
        title: "Review and Submit",
        tooltip: TT_REVIEW_SUBMIT,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
    {
        title: "Delete",
        tooltip: TT_DELETE,
        center: false,
        enableSort: false,
        sortId: "1",
        xlOnly: false,
    },
]

interface IDraftApplicationTableProps {
    searchResults: IDraftApplications | undefined
    orderBy: string
    orderDirection: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    isLoading: boolean
    onSortChange: (sortKey: string) => void
    mutate: KeyedMutator<IDraftApplicationListResponse[]>
}

/**
 * Displays listed drafts. Enables ordering by clicking column headers
 */
const InstallerDraftTable = ({
    searchResults,
    orderBy,
    orderDirection,
    setOrderBy,
    isLoading,
    onSortChange,
    mutate,
}: IDraftApplicationTableProps): ReactElement => {
    const handleRequestSort = (
        event_: MouseEvent<unknown>,
        property: keyof IDraftApplications,
        sortKey: string
    ): void => {
        const isAsc = orderBy === property && orderDirection === ASC
        setOrderBy(sortKey, isAsc ? DESC : ASC)
        onSortChange(sortKey)
    }
    const createSortHandler =
        (property: keyof IDraftApplications, sortKey: string) =>
        (event: MouseEvent<unknown>) => {
            handleRequestSort(event, property, sortKey)
        }

    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(searchResults)) {
            return searchResults.length === 0
        }
        if (!searchResults) {
            return true
        }
        return false
    }

    return emptySearch() ? (
        <AppPaper>
            <ZeroApplicationsMsg />
        </AppPaper>
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <AvatarHeadingCell />
                        {HEADINGS.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.tooltip}
                                key={heading.title}
                            >
                                <AppTableCell
                                    sortDirection={
                                        orderBy === heading.sortId
                                            ? (orderDirection as SortDirection)
                                            : false
                                    }
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === heading.title}
                                        direction={
                                            orderBy === heading.sortId
                                                ? (orderDirection as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                                : (ASC as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                        }
                                        onClick={createSortHandler(
                                            heading.sortId as keyof IDraftApplications,
                                            heading.sortKey as string
                                        )}
                                        disabled={!heading.enableSort}
                                        sx={{
                                            "& .MuiTableSortLabel-icon": {
                                                opacity: 0,
                                            },
                                            "&:hover .MuiTableSortLabel-icon, &.Mui-active .MuiTableSortLabel-icon":
                                                {
                                                    opacity: 1,
                                                },
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                        {orderBy === heading.sortId ? (
                                            <Box
                                                component="span"
                                                sx={visuallyHidden}
                                            >
                                                {orderDirection === DESC
                                                    ? "sorted descending"
                                                    : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`applicationTable${v}`}
                              >
                                  <AppTableCell>
                                      <Skeleton
                                          variant="circular"
                                          width={40}
                                          height={40}
                                      />
                                  </AppTableCell>
                                  {Array.from(
                                      Array(HEADINGS.length).keys()
                                  ).map((num) => (
                                      <AppTableCell key={num}>
                                          <AppSkeleton />
                                      </AppTableCell>
                                  ))}
                              </AppTableContentRow>
                          ))
                        : (searchResults as IDraftApplications).map(
                              (draftApplication) => (
                                  <InstallerDraftRow
                                      key={draftApplication.draftId}
                                      draftApplication={draftApplication}
                                      mutate={mutate}
                                  />
                              )
                          )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default InstallerDraftTable
